import { countries } from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { Currency } from "@redotech/money/currencies";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import {
  FedexMfaOptionType,
  SmartPostHub,
  smartPostHubs,
} from "@redotech/redo-fulfillment-carrier-account-common/fedex-customer-registration";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { IDefaultCarrierCredentials } from "@redotech/redo-fulfillment-rate-common/create-carrier-account";
import { t, type Translate, useTranslation } from "@redotech/redo-i18n";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right.svg";
import AsteriskIcon from "@redotech/redo-icons/asterisk-01_filled.svg";
import FileSearchIcon from "@redotech/redo-icons/file-search-02.svg";
import MailIcon from "@redotech/redo-icons/mail-01.svg";
import MessageIcon from "@redotech/redo-icons/message-text-square-02.svg";
import PhoneIcon from "@redotech/redo-icons/phone-01.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import {
  JSXElementConstructor,
  memo,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Flex } from "../../flex";
import { LogoSize, ShippingLogo } from "../../fulfillment/common/shipping-logo";
import { LegalAgreementContent } from "../../legal-agreement-modal";
import { Text } from "../../text";
import { RedoButton } from "../buttons/redo-button";
import { RedoCheckbox } from "../checkbox/redo-checkbox";
import { Layout, RedoCheckboxCard } from "../checkbox/redo-checkbox-card";
import { RedoFeaturedIcon } from "../featured-icon/redo-featured-icon";
import {
  FileUploadState,
  RedoFileUpload,
} from "../file-upload/redo-file-upload";
import { RedoCurrencyInput } from "../input/redo-currency-input";
import { RedoDateInput } from "../input/redo-date-input";
import { RedoTextInput } from "../input/redo-text-input";
import { RedoSpinner } from "../loading/redo-spinner";
import { RedoBaseModal } from "../modal/redo-base-modal";
import * as redoModalCss from "../modal/redo-modal.module.css";
import { RedoProgressBar } from "../progress-bar/redo-progress-bar";
import { RedoCurrencySelector } from "../select-dropdown/redo-currency-selector";
import { RedoSingleSelectDropdownInput } from "../select-dropdown/redo-single-select-dropdown-input";
import { InfoTooltip } from "../tooltip/info-tooltip";
import {
  fedexAccountDefaults,
  FedExAccountInput,
  FedExAccountValue,
  fedexMfaInvoiceDefaults,
  fedexMfaInvoiceInput,
  FedexMfaInvoiceValue,
  fedexMfaSecureCodeDefaults,
  fedexMfaSecureCodeInput,
  FedexMfaSecureCodeValue,
  FedExParsedInvoiceAutofill,
} from "./fedex-account-inputs";

const ALLOWED_FEDEX_COUNTRY_CODES = new Set(["US", "CA"]);

export const FedExLegalAgreementConfig = {
  description: t("I accept the terms of FedEx EULA to start shipping."),
  imageUrl: "https://assets.getredo.com/fedex_end_user_agreement.jpg",
  requireScroll: true,
  title: t("FedEx End User License Agreement"),
};

export enum FedExMfaStep {
  END_USER_LICENSE_AGREEMENT = "END_USER_LICENSE_AGREEMENT",
  INVOICE_PREFILL = "INVOICE_PREFILL",
  ACCOUNT_INFO = "ACCOUNT_INFO",
  MFA_METHOD = "MFA_METHOD",
  PIN_DELIVERY = "PIN_DELIVERY",
  MFA_DATA = "MFA_DATA",
  USE_MFA_BACKUP = "USE_MFA_BACKUP",
}

function fedexStepTitle(step: FedExMfaStep, t: Translate): string {
  switch (step) {
    case FedExMfaStep.END_USER_LICENSE_AGREEMENT:
      return t("Accept the FedEx End User License Agreement");
    case FedExMfaStep.INVOICE_PREFILL:
      return t("Upload a FedEx Invoice", { context: "FedEx setup step" });
    case FedExMfaStep.ACCOUNT_INFO:
      return t("Provide Your FedEx Account Information");
    case FedExMfaStep.MFA_METHOD:
      return t("Choose Your Verification Method", {
        context: "FedEx setup step",
      });
    case FedExMfaStep.PIN_DELIVERY:
      return t("Choose Your PIN Delivery Method");
    case FedExMfaStep.MFA_DATA:
      return t("Verify Your Account", { context: "FedEx setup step" });
    case FedExMfaStep.USE_MFA_BACKUP:
      return t("Use MFA Backup", { context: "FedEx setup step" });
  }
}

const stepToStepNumber: Record<string, number> = {
  [FedExMfaStep.INVOICE_PREFILL]: 1,
  [FedExMfaStep.ACCOUNT_INFO]: 2,
  [FedExMfaStep.MFA_METHOD]: 3,
  [FedExMfaStep.PIN_DELIVERY]: 4,
  [FedExMfaStep.MFA_DATA]: 5,
};

const stepToStepsCompleted = (currentStep: FedExMfaStep) => {
  const stepNumber = stepToStepNumber[currentStep];
  if (stepNumber === undefined) {
    return 0;
  }
  return stepNumber - 1;
};

// Ignore the EULA and MFA backup steps in the visible progress count.
const numVisibleSteps = 5;

interface FedExMFAModalProps {
  "data-dd-action-name"?: string;
  isOpen: boolean;
  currentStep: FedExMfaStep;
  loading: boolean;
  defaultTeamInfo: IDefaultCarrierCredentials;
  setCurrentStep: (step: FedExMfaStep) => void;
  showSmartPostHubId: boolean;
  onClose: () => void;
  onAccountInfoSubmit: (accountInfo: FedExAccountValue) => void;
  onEndUserLicenseAgreementSubmit: () => void;
  onPinGenerationSubmit: (deliveryOption: "SMS" | "CALL" | "EMAIL") => void;
  onMfaBackupSubmit: (pin: string) => void;
  onMfaDataSubmit: (
    mfaData: FedexMfaInvoiceValue | FedexMfaSecureCodeValue,
  ) => void;
  onInvoiceUpload?: (file: File | null) => void | Promise<void>;
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadPending?: boolean;
  invoiceUploadError?: string | null;
}

export const FedExMFAModal = memo(function FedExMFAModal({
  isOpen,
  currentStep,
  loading,
  defaultTeamInfo,
  setCurrentStep,
  showSmartPostHubId,
  onClose,
  onAccountInfoSubmit,
  onPinGenerationSubmit,
  onMfaDataSubmit,
  onMfaBackupSubmit,
  onEndUserLicenseAgreementSubmit,
  onInvoiceUpload,
  invoiceAutofill,
  invoiceUploadPending = false,
  invoiceUploadError,
  "data-dd-action-name": actionName,
}: FedExMFAModalProps) {
  const handleAccountInfoSubmit = useHandler(
    (accountInfo: FedExAccountValue) => {
      onAccountInfoSubmit(accountInfo);
    },
  );

  const handlePinGenerationSubmit = useHandler(
    (deliveryOption: "SMS" | "CALL" | "EMAIL") => {
      onPinGenerationSubmit(deliveryOption);
    },
  );

  const handleMfaDataSubmit = useHandler(
    (mfaData: FedexMfaInvoiceValue | FedexMfaSecureCodeValue) => {
      onMfaDataSubmit(mfaData);
    },
  );

  const handleMfaBackupSubmit = useHandler((pin: string) => {
    onMfaBackupSubmit(pin);
  });

  return (
    <RedoBaseModal
      data-dd-action-name={actionName}
      isOpen={isOpen}
      onModalCloseRequested={onClose}
    >
      <FedExMFAModalHeader currentStep={currentStep} onClose={onClose} />
      <FedExMFAModalContent
        currentStep={currentStep}
        defaultTeamInfo={defaultTeamInfo}
        invoiceAutofill={invoiceAutofill}
        invoiceUploadError={invoiceUploadError}
        invoiceUploadPending={invoiceUploadPending}
        loading={loading}
        onAccountInfoSubmit={handleAccountInfoSubmit}
        onEndUserLicenseAgreementSubmit={onEndUserLicenseAgreementSubmit}
        onInvoiceUpload={onInvoiceUpload}
        onMfaBackupSubmit={handleMfaBackupSubmit}
        onMfaDataSubmit={handleMfaDataSubmit}
        onPinGenerationSubmit={handlePinGenerationSubmit}
        setCurrentStep={setCurrentStep}
        showSmartPostHubId={showSmartPostHubId}
      />
    </RedoBaseModal>
  );
});

const FedExMFAModalHeader = memo(function FedExMFAModalHeader({
  onClose,
  currentStep,
}: {
  onClose: () => void;
  currentStep: FedExMfaStep;
}) {
  const { t } = useTranslation();
  const showStepNumber =
    currentStep !== FedExMfaStep.END_USER_LICENSE_AGREEMENT &&
    currentStep !== FedExMfaStep.USE_MFA_BACKUP;
  return (
    <Flex align="center" justify="space-between" p="2xl">
      <Flex dir="row" gap="md">
        <Flex align="center" dir="column" gap="xs" justify="center" pr="md">
          <ShippingLogo carrier={Carriers.FEDEX} size={LogoSize.LARGE} />
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="md" fontWeight="medium">
            {t("FedEx multi-factor authentication setup", {
              context: "FedEx modal",
            })}
          </Text>
          {showStepNumber && (
            <Flex align="center" dir="row" gap="md">
              <Text fontSize="sm" fontWeight="thin" textColor="secondary">
                Step {stepToStepNumber[currentStep]} of {numVisibleSteps}:{" "}
                {fedexStepTitle(currentStep, t)}
              </Text>
              <InfoTooltip
                boxSize={16}
                tooltip={{
                  title: (
                    <Text
                      fontSize="sm"
                      fontWeight="thin"
                      textAlign="left"
                      w="full"
                    >
                      Before setup: Accept the FedEx End User License Agreement
                      <br />
                      <br /> Step 1: Optionally upload a recent FedEx invoice
                      for prefill
                      <br />
                      <br /> Step 2: Provide your FedEx account information
                      <br />
                      <br /> Step 3: Choose a verification method (invoice or
                      secure pin)
                      <br />
                      <br /> Step 4: If pin is selected, choose your PIN
                      delivery method (SMS, email, or voice call)
                      <br />
                      <br /> Step 5: Authenticate your account
                    </Text>
                  ),
                }}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <RedoButton
        aria-label={t("Close", { context: "FedEx setup dismiss" })}
        className={redoModalCss.closeButton}
        hierarchy="tertiary"
        IconLeading={CloseIcon}
        onClick={onClose}
        size="md"
      />
    </Flex>
  );
});

const FedExMFAModalContent = memo(function FedExMFAModalContent({
  defaultTeamInfo,
  loading,
  currentStep,
  showSmartPostHubId,
  setCurrentStep,
  onAccountInfoSubmit,
  onEndUserLicenseAgreementSubmit,
  onPinGenerationSubmit,
  onMfaDataSubmit,
  onMfaBackupSubmit,
  invoiceAutofill,
  invoiceUploadError,
  invoiceUploadPending,
  onInvoiceUpload,
}: {
  defaultTeamInfo: IDefaultCarrierCredentials;
  loading: boolean;
  currentStep: FedExMfaStep;
  showSmartPostHubId: boolean;
  setCurrentStep: (step: FedExMfaStep) => void;
  onAccountInfoSubmit: (accountInfo: FedExAccountValue) => void;
  onEndUserLicenseAgreementSubmit: () => void;
  onPinGenerationSubmit: (deliveryOption: "SMS" | "CALL" | "EMAIL") => void;
  onMfaDataSubmit: (
    mfaData: FedexMfaInvoiceValue | FedexMfaSecureCodeValue,
  ) => void;
  onMfaBackupSubmit: (pin: string) => void;
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadError?: string | null;
  invoiceUploadPending: boolean;
  onInvoiceUpload?: (file: File | null) => void | Promise<void>;
}) {
  const showProgress = currentStep !== FedExMfaStep.USE_MFA_BACKUP;
  const [selectedMfaType, setSelectedMfaType] = useState<FedexMfaOptionType>(
    FedexMfaOptionType.INVOICE,
  );

  const handleEndUserLicenseAgreementSubmit = useHandler(() => {
    onEndUserLicenseAgreementSubmit();
  });

  const handleAccountInfoSubmit = useHandler(
    (accountInfo: FedExAccountValue) => {
      onAccountInfoSubmit(accountInfo);
    },
  );

  const handleMfaMethodSubmit = useHandler((mfaMethod: FedexMfaOptionType) => {
    setSelectedMfaType(mfaMethod);
    if (mfaMethod === FedexMfaOptionType.SECURE_CODE) {
      setCurrentStep(FedExMfaStep.PIN_DELIVERY);
    } else {
      setCurrentStep(FedExMfaStep.MFA_DATA);
    }
  });

  const handlePinDeliverySubmit = useHandler(
    (deliveryOption: "SMS" | "CALL" | "EMAIL") => {
      onPinGenerationSubmit(deliveryOption);
    },
  );

  const handleMfaDataSubmit = useHandler(
    (mfaData: FedexMfaInvoiceValue | FedexMfaSecureCodeValue) => {
      onMfaDataSubmit(mfaData);
    },
  );

  const handleUseMfaBackupSubmit = useHandler((pin: string) => {
    onMfaBackupSubmit(pin);
  });

  const percentage = useMemo(() => {
    return (stepToStepsCompleted(currentStep) / numVisibleSteps) * 100;
  }, [currentStep]);

  return (
    <Flex dir="column" gap="lg" justify="center">
      {showProgress && (
        <RedoProgressBar percentage={percentage} style="extra-thin" />
      )}
      <Flex gap="md" pb="2xl" px="2xl">
        {currentStep === FedExMfaStep.USE_MFA_BACKUP && (
          <FedexUseMfaBackupStep
            loading={loading}
            onSubmit={handleUseMfaBackupSubmit}
          />
        )}
        {currentStep === FedExMfaStep.END_USER_LICENSE_AGREEMENT && (
          <FedexEndUserLicenseAgreementStep
            loading={loading}
            onSubmit={handleEndUserLicenseAgreementSubmit}
          />
        )}
        {currentStep === FedExMfaStep.INVOICE_PREFILL && (
          <FedexInvoicePrefillStep
            invoiceAutofill={invoiceAutofill}
            invoiceUploadError={invoiceUploadError}
            invoiceUploadPending={invoiceUploadPending}
            loading={loading}
            onContinue={() => setCurrentStep(FedExMfaStep.ACCOUNT_INFO)}
            onInvoiceUpload={onInvoiceUpload}
          />
        )}
        {currentStep === FedExMfaStep.ACCOUNT_INFO && (
          <FedExAccountInfoStep
            defaultTeamInfo={defaultTeamInfo}
            invoiceAutofill={invoiceAutofill}
            invoiceUploadError={invoiceUploadError}
            loading={loading}
            onSubmit={handleAccountInfoSubmit}
            showSmartPostHubId={showSmartPostHubId}
          />
        )}
        {currentStep === FedExMfaStep.MFA_METHOD && (
          <FedExMfaMethodStep onSubmit={handleMfaMethodSubmit} />
        )}
        {currentStep === FedExMfaStep.PIN_DELIVERY && (
          <FedExPinDeliveryStep
            loading={loading}
            onBack={() => setCurrentStep(FedExMfaStep.MFA_METHOD)}
            onSubmit={handlePinDeliverySubmit}
          />
        )}
        {currentStep === FedExMfaStep.MFA_DATA && (
          <FedexMfaDataStep
            invoiceAutofill={invoiceAutofill}
            invoiceUploadError={invoiceUploadError}
            loading={loading}
            onBack={() => {
              setCurrentStep(
                selectedMfaType === FedexMfaOptionType.SECURE_CODE
                  ? FedExMfaStep.PIN_DELIVERY
                  : FedExMfaStep.MFA_METHOD,
              );
            }}
            onSubmit={handleMfaDataSubmit}
            selectedMfaType={selectedMfaType}
          />
        )}
      </Flex>
    </Flex>
  );
});

const FedexUseMfaBackupStep = memo(function FedexUseMfaBackupStep({
  loading,
  onSubmit,
}: {
  loading: boolean;
  onSubmit: (pin: string) => void;
}) {
  const { t } = useTranslation();
  const formInput = useInput(
    fedexMfaSecureCodeInput,
    fedexMfaSecureCodeDefaults(),
  );
  return (
    <Flex dir="column" gap="md" height="full" w="full">
      <Text fontSize="sm" textColor="secondary">
        You have reached the maximum number of attempts to verify your account
        automatically. Please call FedEx Customer Service and they will assist
        you in verifying your account. They will give you a PIN that you can
        input below to verify your account.
      </Text>
      <RedoTextInput
        errorText={
          formInput.changed
            ? formInput.inputs.secureCode.errors.join(", ")
            : undefined
        }
        label={t("PIN", { context: "FedEx verification" })}
        placeholder="123456"
        setValue={formInput.inputs.secureCode.setValue}
        size="sm"
        value={formInput.inputs.secureCode.value}
      />
      <Footer
        continueDisabled={formInput.allErrors.length > 0}
        loading={loading}
        onContinue={() => onSubmit(formInput.inputs.secureCode.value)}
      />
    </Flex>
  );
});

const FedexEndUserLicenseAgreementStep = memo(
  function FedexEndUserLicenseAgreementStep({
    onSubmit,
    loading,
  }: {
    onSubmit: () => void;
    loading: boolean;
  }) {
    const [accepted, setAccepted] = useState(false);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

    return (
      <Flex dir="column" gap="md" height="full" w="full">
        <LegalAgreementContent
          accepted={accepted}
          description={FedExLegalAgreementConfig.description}
          hasScrolledToBottom={hasScrolledToBottom}
          imageUrl={FedExLegalAgreementConfig.imageUrl}
          requireScroll={FedExLegalAgreementConfig.requireScroll}
          setAccepted={setAccepted}
          setHasScrolledToBottom={setHasScrolledToBottom}
        />
        <Footer
          continueDisabled={!accepted}
          loading={loading}
          onContinue={onSubmit}
        />
      </Flex>
    );
  },
);

const FedexInvoicePrefillStep = memo(function FedexInvoicePrefillStep({
  invoiceAutofill,
  invoiceUploadError,
  invoiceUploadPending,
  loading,
  onContinue,
  onInvoiceUpload,
}: {
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadError?: string | null;
  invoiceUploadPending: boolean;
  loading: boolean;
  onContinue: () => void;
  onInvoiceUpload?: (file: File | null) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileUploadState<File>[]>([]);

  const handleFilesAdded = useHandler((fileList: FileList | null) => {
    if (!fileList) {
      return;
    }
    const file = fileList[0];
    if (!file) {
      return;
    }
    setFiles([{ value: file, name: file.name, bytesTotal: file.size }]);
    void onInvoiceUpload?.(file);
  });

  const handleFileDeleted = useHandler(() => {
    setFiles([]);
    void onInvoiceUpload?.(null);
  });

  return (
    <Flex dir="column" gap="md" height="full" w="full">
      <Text fontSize="sm" textColor="secondary">
        Uploading a recent FedEx invoice PDF is optional. If you upload one,
        Redo AI will try to verify the account automatically and prefill the
        manual form with what it finds.
      </Text>
      <Text fontSize="sm" textColor="secondary">
        You can skip this step and enter the account and invoice details
        yourself.
      </Text>
      <RedoFileUpload
        accept="application/pdf"
        acceptMessage="PDF"
        files={files}
        fullWidth
        handleFileDeleted={handleFileDeleted}
        handleFilesAdded={handleFilesAdded}
        nFilesToAccept={1}
      />
      {invoiceUploadPending && (
        <Flex align="center" gap="sm">
          <RedoSpinner size="sm" style="track" />
          <Text fontSize="sm">
            {t("Reading invoice and attempting verification...")}
          </Text>
        </Flex>
      )}
      {invoiceUploadError && (
        <Text fontSize="sm" textColor="error">
          {invoiceUploadError}
        </Text>
      )}
      {invoiceAutofill?.invoiceNumber && !invoiceUploadPending && (
        <Text fontSize="sm" textColor="secondary">
          {t(
            "Invoice parsed. If automatic verification needs review, the next form will be prefilled for you.",
          )}
        </Text>
      )}
      <Footer
        continueDisabled={invoiceUploadPending || files.length === 0}
        loading={loading}
        onContinue={onContinue}
        onSkip={onContinue}
        showSkip
        skipText={t("Skip AI", { context: "FedEx invoice" })}
      />
    </Flex>
  );
});

const FedexMfaDataStep = memo(function FedexMfaDataStep({
  invoiceAutofill,
  invoiceUploadError,
  onSubmit,
  onBack,
  selectedMfaType,
  loading,
}: {
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadError?: string | null;
  onSubmit: (mfaData: FedexMfaInvoiceValue | FedexMfaSecureCodeValue) => void;
  onBack: () => void;
  selectedMfaType: FedexMfaOptionType;
  loading: boolean;
}) {
  const handleMfaInvoiceSubmit = useHandler((mfaData: FedexMfaInvoiceValue) => {
    onSubmit(mfaData);
  });

  const handleMfaPinSubmit = useHandler((mfaData: FedexMfaSecureCodeValue) => {
    onSubmit(mfaData);
  });

  return (
    <Flex dir="column" gap="md" height="full" w="full">
      {selectedMfaType === FedexMfaOptionType.INVOICE && (
        <FedexMfaInvoiceStep
          invoiceAutofill={invoiceAutofill}
          invoiceUploadError={invoiceUploadError}
          loading={loading}
          onBack={onBack}
          onSubmit={handleMfaInvoiceSubmit}
        />
      )}
      {selectedMfaType === FedexMfaOptionType.SECURE_CODE && (
        <FedexMfaPinStep
          loading={loading}
          onBack={onBack}
          onSubmit={handleMfaPinSubmit}
        />
      )}
    </Flex>
  );
});

const FedexMfaInvoiceStep = memo(function FedexMfaInvoiceStep({
  invoiceAutofill,
  invoiceUploadError,
  onBack,
  onSubmit,
  loading,
}: {
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadError?: string | null;
  onBack: () => void;
  onSubmit: (mfaData: FedexMfaInvoiceValue) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const formInput = useInput(
    fedexMfaInvoiceInput,
    fedexMfaInvoiceDefaults(invoiceAutofill),
  );

  const handleSubmit = useHandler(() => {
    onSubmit(formInput.value);
  });

  const isContinueDisabled = useMemo(() => {
    return formInput.allErrors.length > 0;
  }, [formInput]);

  return (
    <Flex dir="column" gap="xl" height="full" w="full">
      <Text fontSize="sm" textColor="secondary">
        Please provide information from a recent FedEx invoice (last 90 days) to
        verify your account.
      </Text>
      {invoiceUploadError && (
        <Text fontSize="sm" textColor="error">
          {invoiceUploadError}
        </Text>
      )}

      <RedoTextInput
        errorText={
          formInput.inputs.invoiceNumber.changed
            ? formInput.inputs.invoiceNumber.errors.join(", ")
            : undefined
        }
        label={t("Invoice Number", { context: "FedEx invoice" })}
        placeholder="234562278"
        setValue={formInput.inputs.invoiceNumber.setValue}
        size="sm"
        value={formInput.inputs.invoiceNumber.value}
      />
      <RedoDateInput
        errorText={
          formInput.inputs.invoiceDate.changed
            ? formInput.inputs.invoiceDate.errors.join(", ")
            : undefined
        }
        label={t("Invoice Date", { context: "FedEx invoice" })}
        setValue={formInput.inputs.invoiceDate.setValue}
        size="sm"
        value={formInput.inputs.invoiceDate.value}
      />
      <Flex dir="row" gap="md">
        <Flex dir="column" flex="1" gap="sm" w="half">
          <RedoCurrencyInput
            errorText={
              formInput.inputs.invoiceAmount.changed
                ? formInput.inputs.invoiceAmount.errors.join(", ")
                : undefined
            }
            label={t("Invoice Amount", { context: "FedEx invoice" })}
            placeholder={0}
            setValue={(value) =>
              formInput.inputs.invoiceAmount.setValue(
                value.amount?.toString() ?? "0",
              )
            }
            size="sm"
            value={
              formInput.inputs.invoiceAmount.value
                ? {
                    amount: parseFloat(formInput.inputs.invoiceAmount.value),
                    currency: Currency.USD,
                  }
                : { amount: undefined, currency: Currency.USD }
            }
          />
        </Flex>
        <Flex dir="column" flex="1" gap="sm" w="half">
          <RedoCurrencySelector
            label={t("Invoice Currency", { context: "FedEx invoice" })}
            placeholder={t("Select currency", { context: "FedEx invoice" })}
            setValue={(value) =>
              formInput.inputs.invoiceCurrency.setValue(value ?? Currency.USD)
            }
            value={formInput.inputs.invoiceCurrency.value}
          />
        </Flex>
      </Flex>
      <Footer
        continueDisabled={isContinueDisabled}
        continueText={t("Submit", { context: "FedEx setup action" })}
        loading={loading}
        onBack={onBack}
        onContinue={handleSubmit}
        showBack
      />
    </Flex>
  );
});

const FedexMfaPinStep = memo(function FedexMfaPinStep({
  onBack,
  onSubmit,
  loading,
}: {
  onBack: () => void;
  onSubmit: (mfaData: FedexMfaSecureCodeValue) => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const formInput = useInput(
    fedexMfaSecureCodeInput,
    fedexMfaSecureCodeDefaults(),
  );

  const handleSubmit = useHandler(() => {
    onSubmit(formInput.value);
  });

  const isContinueDisabled = useMemo(() => {
    return formInput.allErrors.length > 0;
  }, [formInput]);

  return (
    <>
      <Text fontSize="md" fontWeight="medium">
        {t("Enter Verification Code", { context: "FedEx verification" })}
      </Text>

      <RedoTextInput
        errorText={
          formInput.inputs.secureCode.changed
            ? formInput.inputs.secureCode.errors.join(", ")
            : undefined
        }
        label={t("Verification Code", { context: "FedEx verification" })}
        placeholder="123456"
        setValue={formInput.inputs.secureCode.setValue}
        size="sm"
        value={formInput.inputs.secureCode.value}
      />
      <Footer
        continueDisabled={isContinueDisabled}
        continueText={t("Submit", { context: "FedEx setup action" })}
        loading={loading}
        onBack={onBack}
        onContinue={handleSubmit}
        showBack
      />
    </>
  );
});

const FedExMfaMethodStep = memo(function FedExMfaMethodStep({
  onSubmit,
}: {
  onSubmit: (mfaMethod: FedexMfaOptionType) => void;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="2xl">
      <Text fontSize="sm" textColor="secondary">
        {t(
          "To complete your account verification, please choose one of the following methods:",
        )}
      </Text>

      <Flex dir="row" gap="xl">
        <Flex
          dir="column"
          flex={1}
          gap="md"
          onClick={() => {
            onSubmit(FedexMfaOptionType.SECURE_CODE);
          }}
          w="half"
        >
          <FedexMfaMethodItem
            description={t(
              "FedEx will send you a pin via your choice of email, text message, or voice call.",
            )}
            icon={AsteriskIcon}
            onClick={() => onSubmit(FedexMfaOptionType.SECURE_CODE)}
            title={t("PIN", { context: "FedEx verification" })}
          />
        </Flex>
        <Flex
          dir="column"
          flex={1}
          gap="md"
          onClick={() => onSubmit(FedexMfaOptionType.INVOICE)}
          w="half"
        >
          <FedexMfaMethodItem
            description={t(
              "Verify using a recent FedEx invoice (last 90 days). You'll need the invoice number, date, and amount.",
            )}
            icon={FileSearchIcon}
            onClick={() => onSubmit(FedexMfaOptionType.INVOICE)}
            title={t("Invoice verification", { context: "FedEx verification" })}
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

const FedexMfaMethodItem = memo(function FedexMfaMethodItem({
  icon,
  title,
  description,
  onClick,
}: {
  icon: JSXElementConstructor<unknown>;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <Flex
      borderColor="primary"
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      cursor="pointer"
      dir="column"
      gap="xl"
      height="full"
      onClick={onClick}
      p="2xl"
    >
      <RedoFeaturedIcon color="brand" Icon={icon} size="md" />
      <Text fontSize="md" fontWeight="medium">
        {title}
      </Text>
      <Text fontSize="sm" fontWeight="thin" textColor="secondary">
        {description}
      </Text>
      <Flex align="flex-end" dir="row" gap="md" grow={1} justify="flex-end">
        <ArrowRightIcon />
      </Flex>
    </Flex>
  );
});

const FedExPinDeliveryStep = memo(function FedExPinDeliveryStep({
  onSubmit,
  onBack,
  loading,
}: {
  onSubmit: (deliveryOption: "SMS" | "CALL" | "EMAIL") => void;
  onBack: () => void;
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<
    "SMS" | "CALL" | "EMAIL" | null
  >(null);

  const deliveryOptions = [
    {
      id: "SMS" as const,
      icon: MessageIcon,
      title: t("Text Message (SMS)", { context: "FedEx PIN delivery" }),
      description:
        "Receive your PIN via a text message to the phone number registered with your FedEx account",
    },
    {
      id: "EMAIL" as const,
      icon: MailIcon,
      title: t("Email", { context: "FedEx PIN delivery" }),
      description:
        "Receive your PIN via email to the email address registered with your FedEx account",
    },
    {
      id: "CALL" as const,
      icon: PhoneIcon,
      title: t("Voice Call", { context: "FedEx PIN delivery" }),
      description:
        "Receive your PIN via automated voice call to the phone number registered with your FedEx account",
    },
  ];

  return (
    <Flex dir="column" gap="xl">
      <Text fontSize="sm" textColor="secondary">
        {t("Select how you would like to receive your FedEx account PIN:")}
      </Text>

      <Flex dir="column" gap="md">
        {deliveryOptions.map((option) => (
          <RedoCheckboxCard
            checked={selectedOption === option.id}
            inputSize="sm"
            inputType="radio"
            key={option.id}
            layout={Layout.LEFT}
            onClick={() => {
              setSelectedOption(option.id);
            }}
            title={
              <Flex dir="column" gap="xs" grow={1}>
                <Text fontSize="sm" fontWeight="medium">
                  {option.title}
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {option.description}
                </Text>
              </Flex>
            }
          />
        ))}
      </Flex>

      <Footer
        backDisabled={false}
        continueDisabled={!selectedOption || loading}
        loading={loading}
        onBack={onBack}
        onContinue={() => {
          if (selectedOption) {
            onSubmit(selectedOption);
          }
        }}
        showBack
      />
    </Flex>
  );
});

const FedExAccountInfoStep = memo(function FedExAccountInfoStep({
  defaultTeamInfo,
  showSmartPostHubId,
  invoiceAutofill,
  invoiceUploadError,
  onSubmit,
  loading,
}: {
  defaultTeamInfo: IDefaultCarrierCredentials;
  showSmartPostHubId: boolean;
  invoiceAutofill?: FedExParsedInvoiceAutofill | null;
  invoiceUploadError?: string | null;
  loading: boolean;
  onSubmit: (accountInfo: FedExAccountValue) => void;
}) {
  const { t } = useTranslation();
  const formInput = useInput(
    FedExAccountInput,
    fedexAccountDefaults(defaultTeamInfo, invoiceAutofill),
  );
  const {
    accountNumber,
    city,
    countryCode,
    customerName,
    postalCode,
    stateOrProvinceCode,
    streetLine1,
    streetLine2,
  } = formInput.inputs;
  const currentCountryCode = formInput.inputs.countryCode.value;
  const currentProvinceCode = formInput.inputs.stateOrProvinceCode.value;
  const availableProvinceOptions = useMemo(
    () =>
      shopifyProvinces
        .filter((province) => province.countryCode === currentCountryCode)
        .map((province) => ({
          id: province.provinceCode,
          text: province.province,
          type: "text" as const,
          value: province.provinceCode,
        })),
    [currentCountryCode],
  );

  const selectedProvinceItem = useMemo(() => {
    if (!currentProvinceCode) {
      return undefined;
    }

    const selectedProvince = availableProvinceOptions.find(
      (province) => province.value === currentProvinceCode,
    );

    return (
      selectedProvince ??
      ({
        id: currentProvinceCode,
        type: "text" as const,
        text: currentProvinceCode,
        value: currentProvinceCode,
      } as const)
    );
  }, [availableProvinceOptions, currentProvinceCode]);

  useEffect(() => {
    if (!invoiceAutofill) {
      return;
    }

    accountNumber.setValue(invoiceAutofill.accountNumber ?? "");
    customerName.setValue(invoiceAutofill.accountName ?? "");
    streetLine1.setValue(invoiceAutofill.billingAddress?.streetLine1 ?? "");
    streetLine2.setValue(invoiceAutofill.billingAddress?.streetLine2 ?? "");
    city.setValue(invoiceAutofill.billingAddress?.city ?? "");
    stateOrProvinceCode.setValue(
      invoiceAutofill.billingAddress?.stateOrProvinceCode ?? "",
    );
    postalCode.setValue(invoiceAutofill.billingAddress?.postalCode ?? "");
    countryCode.setValue(
      invoiceAutofill.billingAddress?.countryCode ??
        defaultTeamInfo.address_country,
    );
  }, [
    accountNumber,
    city,
    countryCode,
    customerName,
    defaultTeamInfo.address_country,
    invoiceAutofill,
    postalCode,
    stateOrProvinceCode,
    streetLine1,
    streetLine2,
  ]);

  const handleSubmit = useHandler(() => {
    onSubmit(formInput.value);
  });

  const isContinueDisabled = useMemo(() => {
    return formInput.allErrors.length > 0;
  }, [formInput]);

  return (
    <Flex dir="column" gap="xl" height="full" w="full">
      <Text fontSize="sm">
        Please provide your FedEx account information to validate your account
        ownership.
      </Text>
      {invoiceUploadError && (
        <Text fontSize="sm" textColor="error">
          {invoiceUploadError}
        </Text>
      )}

      <RedoTextInput
        errorText={
          formInput.inputs.merchantFacingName.changed
            ? formInput.inputs.merchantFacingName.errors.join(", ")
            : undefined
        }
        label={t("Name your carrier account", { context: "FedEx account" })}
        placeholder={t("My FedEx Account", { context: "FedEx account name" })}
        setValue={formInput.inputs.merchantFacingName.setValue}
        size="sm"
        value={formInput.inputs.merchantFacingName.value}
      />

      <Flex dir="row" gap="md">
        <Flex dir="column" flex="1" gap="sm">
          <RedoTextInput
            description={t("9-digit FedEx account number", {
              context: "FedEx account",
            })}
            errorText={
              formInput.inputs.accountNumber.changed
                ? formInput.inputs.accountNumber.errors.join(", ")
                : undefined
            }
            label={t("Account Number", { context: "FedEx account" })}
            placeholder="123456789"
            setValue={formInput.inputs.accountNumber.setValue}
            size="sm"
            value={formInput.inputs.accountNumber.value}
          />
        </Flex>

        {showSmartPostHubId && (
          <Flex dir="column" flex="1" gap="sm">
            <FedexSmartPostSelector
              setValue={formInput.inputs.smartPostHubId.setValue}
              value={formInput.inputs.smartPostHubId.value}
            />
          </Flex>
        )}

        <Flex dir="column" flex="1" gap="sm">
          <RedoTextInput
            errorText={
              formInput.inputs.customerName.changed
                ? formInput.inputs.customerName.errors.join(", ")
                : undefined
            }
            label={t("Customer Name", { context: "FedEx account" })}
            placeholder={t("Company Name", {
              context: "FedEx account placeholder",
            })}
            setValue={formInput.inputs.customerName.setValue}
            size="sm"
            value={formInput.inputs.customerName.value}
          />
        </Flex>
      </Flex>
      <Text fontSize="sm" fontWeight="semibold">
        {t("Physical Address", { context: "FedEx address" })}
      </Text>
      <Text fontSize="xs" fontWeight="thin">
        In order for FedEx to verify your account, the physical address must
        exactly match the address on your FedEx account.
      </Text>

      <RedoTextInput
        errorText={
          formInput.inputs.streetLine1.changed
            ? formInput.inputs.streetLine1.errors.join(", ")
            : undefined
        }
        label={t("Street Address", { context: "FedEx address" })}
        placeholder={t("123 Main St", { context: "street address example" })}
        setValue={formInput.inputs.streetLine1.setValue}
        size="sm"
        value={formInput.inputs.streetLine1.value}
      />

      <RedoTextInput
        label={t("Street Address Line 2 (Optional)")}
        placeholder={t("Suite 100", { context: "street address example" })}
        setValue={formInput.inputs.streetLine2.setValue}
        size="sm"
        value={formInput.inputs.streetLine2.value}
      />

      <Flex dir="row" gap="md">
        <Flex dir="column" flex="1" gap="sm">
          <RedoTextInput
            errorText={
              formInput.inputs.city.changed
                ? formInput.inputs.city.errors.join(", ")
                : undefined
            }
            label={t("City", { context: "FedEx address" })}
            placeholder={t("New York", { context: "city example" })}
            setValue={formInput.inputs.city.setValue}
            size="sm"
            value={formInput.inputs.city.value}
          />
        </Flex>

        <Flex dir="column" flex="1" gap="sm">
          <RedoSingleSelectDropdownInput
            label={
              currentCountryCode === "US"
                ? t("State", { context: "FedEx address" })
                : t("Province", { context: "FedEx address" })
            }
            options={availableProvinceOptions}
            optionSelected={({ item }) =>
              formInput.inputs.stateOrProvinceCode.setValue(item.value)
            }
            placeholder={
              currentCountryCode === "US"
                ? t("Select state", { context: "FedEx address" })
                : t("Select province", { context: "FedEx address" })
            }
            selectedItem={selectedProvinceItem}
            size="xs"
          />
        </Flex>
      </Flex>

      <Flex dir="row" gap="md">
        <Flex dir="column" flex="1" gap="sm">
          <RedoTextInput
            errorText={
              formInput.inputs.postalCode.changed
                ? formInput.inputs.postalCode.errors.join(", ")
                : undefined
            }
            label={t("Postal Code", { context: "FedEx address" })}
            placeholder="10011"
            setValue={formInput.inputs.postalCode.setValue}
            size="sm"
            value={formInput.inputs.postalCode.value}
          />
        </Flex>

        <Flex dir="column" flex="1" gap="sm">
          <RedoSingleSelectDropdownInput
            label={t("Country", { context: "FedEx address" })}
            options={countries
              .filter((country) =>
                ALLOWED_FEDEX_COUNTRY_CODES.has(country.code),
              )
              .map((country) => ({
                id: country.code,
                type: "text" as const,
                text: country.name,
                value: country.code,
              }))}
            optionSelected={({ item }) => {
              formInput.inputs.stateOrProvinceCode.setValue("");
              formInput.inputs.countryCode.setValue(item.value);
            }}
            selectedItem={
              formInput.inputs.countryCode.value
                ? {
                    id: formInput.inputs.countryCode.value,
                    type: "text" as const,
                    text:
                      countries.find(
                        (country) =>
                          country.code === formInput.inputs.countryCode.value,
                      )?.name ?? formInput.inputs.countryCode.value,
                    value: formInput.inputs.countryCode.value,
                  }
                : undefined
            }
            size="xs"
          />
        </Flex>
      </Flex>
      <RedoCheckbox
        label={t("This is a residential address")}
        setValue={formInput.inputs.residential.setValue}
        size="xs"
        value={formInput.inputs.residential.value}
      />
      <Footer
        continueDisabled={isContinueDisabled}
        loading={loading}
        onContinue={handleSubmit}
      />
    </Flex>
  );
});

const Footer = memo(function ContinueButton({
  backDisabled = false,
  showBack = false,
  showSkip = false,
  onBack = () => {},
  onSkip = () => {},
  continueDisabled,
  loading,
  onContinue,
  continueText: continueTextProp,
  skipText: skipTextProp,
}: {
  continueDisabled: boolean;
  loading: boolean;
  onContinue: () => void;
  backDisabled?: boolean;
  showBack?: boolean;
  showSkip?: boolean;
  onBack?: () => void;
  onSkip?: () => void;
  continueText?: string;
  skipText?: string;
}) {
  const { t } = useTranslation();
  const continueText =
    continueTextProp ?? t("Continue", { context: "FedEx setup action" });
  const skipText = skipTextProp ?? t("Skip", { context: "FedEx setup action" });
  return (
    <Flex align="flex-end" dir="row" gap="md" justify="flex-end" pt="2xl">
      {showBack && (
        <RedoButton
          disabled={backDisabled}
          hierarchy="secondary"
          onClick={onBack}
          size="sm"
          text={t("Back", { context: "FedEx setup action" })}
          theme="normal"
        />
      )}
      {showSkip && (
        <RedoButton
          disabled={loading}
          hierarchy="secondary"
          onClick={onSkip}
          size="sm"
          text={skipText}
          theme="normal"
        />
      )}
      <RedoButton
        disabled={continueDisabled}
        hierarchy="primary"
        onClick={onContinue}
        pending={loading}
        size="sm"
        text={continueText}
        theme="normal"
      />
    </Flex>
  );
});

const FedexSmartPostSelector = memo(function FedexSmartPostSelector({
  value,
  setValue,
}: {
  value: SmartPostHub["hubId"] | undefined;
  setValue: (value: SmartPostHub["hubId"] | undefined) => void;
}) {
  const { t } = useTranslation();
  const [smartPostHubSearchString, setSmartPostHubSearchString] = useState("");

  const filteredSmartPostHubs = useMemo(
    () =>
      !smartPostHubSearchString
        ? smartPostHubs
        : smartPostHubs.filter(
            (option) =>
              option.name
                .toLowerCase()
                .includes(smartPostHubSearchString.toLowerCase()) ||
              option.acronym
                .toLowerCase()
                .includes(smartPostHubSearchString.toLowerCase()),
          ),
    [smartPostHubSearchString],
  );

  return (
    <RedoSingleSelectDropdownInput
      label={t("FedEx Ground® Economy Hub ID")}
      options={filteredSmartPostHubs.map((hub) => ({
        id: hub.hubId,
        text: hub.name,
        type: "text" as const,
        value: hub.hubId,
      }))}
      optionSelected={({ item }) => setValue(item.value)}
      placeholder={t("Select hub ID (optional)", { context: "FedEx hub" })}
      searchPlaceholder={t("Search hubs...", { context: "FedEx hub" })}
      searchString={smartPostHubSearchString}
      selectedItem={
        value
          ? {
              id: value,
              type: "text" as const,
              text:
                smartPostHubs.find((hub) => hub.hubId === value)?.name ??
                value.toString(),
              value,
            }
          : undefined
      }
      setSearchString={setSmartPostHubSearchString}
      size="xs"
    />
  );
});
